import React, { useState, useEffect } from 'react';
// import 'bootstrap/dist/css/bootstrap.min.css';
// import 'bootstrap-icons/font/bootstrap-icons.css';
import { useRatings } from './RatingsContext';

function PrinciplesAndStandards({ phase }) {
  const { setStandardsRatings } = useRatings();
  const ratingOptions = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
  const isSelfAssessment = phase === 'self-assessment' || 'capture-progress';
  const [standards, setStandards] = useState([
    {
      generic: 'Integrity',
      guidelines: 'Always be truthful and honest in actions, communications, and dealings.',
      weight: '50',
      selfRating: null,
      lineManagerRating: null,
    },
    {
      generic: 'Culture',
      guidelines: 'Collaboration among teams to leverage collective expertise and perspectives.',
      weight: '30',
      selfRating: null,
      lineManagerRating: null,
    },
    {
      generic: 'Problem Solving',
      guidelines: 'Identifying and addressing the root causes of problems rather than addressing symptoms',
      weight: '20',
      selfRating: null,
      lineManagerRating: null,
    },
  ]);
  const [showStandardModal, setShowStandardModal] = useState(false);
  const [newStandard, setNewStandard] = useState({
    generic: '',
    guidelines: '',
    weight: '',
    selfRating: null,
    lineManagerRating: null,
  });
  const [selectedStandardIndex, setSelectedStandardIndex] = useState(null);
  const [isEditingStandard, setIsEditingStandard] = useState(false);

  const handleAddStandard = () => {
    console.log('handleAddStandard called with newStandard:', newStandard);
    const missingFields = [];
    if (!newStandard.generic) missingFields.push('Generic');
    if (!newStandard.guidelines) missingFields.push('Guidelines');
    if (!newStandard.weight) missingFields.push('Weight');

    if (missingFields.length > 0) {
      alert(`Please fill in the following required fields: ${missingFields.join(', ')}.`);
      console.log('Validation failed, missing required fields:', missingFields);
      return;
    }

    console.log('Required fields valid, adding Standard');
    setStandards([...standards, { ...newStandard }]);
    setNewStandard({
      generic: '',
      guidelines: '',
      weight: '',
      selfRating: null,
      lineManagerRating: null,
    });
    setShowStandardModal(false);
    console.log('Standard added, updated standards:', standards);
  };

  const handleEditStandard = (index) => {
    console.log('handleEditStandard called for index:', index);
    setSelectedStandardIndex(index);
    setNewStandard({ ...standards[index] });
    setIsEditingStandard(true);
    setShowStandardModal(true);
  };

  const handleSaveEditStandard = () => {
    console.log('handleSaveEditStandard called with newStandard:', newStandard);
    console.log('selectedStandardIndex:', selectedStandardIndex);
    const missingFields = [];
    if (!newStandard.generic) missingFields.push('Generic');
    if (!newStandard.guidelines) missingFields.push('Guidelines');
    if (!newStandard.weight) missingFields.push('Weight');

    if (missingFields.length > 0) {
      alert(`Please fill in the following required fields: ${missingFields.join(', ')}.`);
      console.log('Validation failed, missing required fields:', missingFields);
      return;
    }

    console.log('Required fields valid, updating Standard');
    const updatedStandards = [...standards];
    updatedStandards[selectedStandardIndex] = { ...newStandard };
    setStandards(updatedStandards);
    setNewStandard({
      generic: '',
      guidelines: '',
      weight: '',
      selfRating: null,
      lineManagerRating: null,
    });
    setShowStandardModal(false);
    setSelectedStandardIndex(null);
    setIsEditingStandard(false);
    console.log('Standard updated, updated standards:', updatedStandards);
  };

  const handleDeleteStandard = () => {
    console.log('handleDeleteStandard called for index:', selectedStandardIndex);
    const updatedStandards = standards.filter((_, index) => index !== selectedStandardIndex);
    setStandards(updatedStandards);
    setNewStandard({
      generic: '',
      guidelines: '',
      weight: '',
      selfRating: null,
      lineManagerRating: null,
    });
    setShowStandardModal(false);
    setSelectedStandardIndex(null);
    setIsEditingStandard(false);
    console.log('Standard deleted, updated standards:', updatedStandards);
  };

  const handleRatingChange = (index, type, value) => {
    console.log(`handleRatingChange called for index: ${index}, type: ${type}, value: ${value}`);
    const updatedStandards = [...standards];
    updatedStandards[index][type] = value ? parseFloat(value) : null;
    setStandards(updatedStandards);
  };

  const calculateRatedPercentage = (type) => {
    if (standards.length === 0) return { rating: null, percentage: null };
    const ratedStandards = standards.filter((standard) => standard[type] !== null).length;
    const percentage = (ratedStandards / standards.length) * 100;
    const averageRating =
      ratedStandards > 0
        ? standards
            .filter((standard) => standard[type] !== null)
            .reduce((sum, standard) => sum + standard[type], 0) / ratedStandards
        : null;
    return {
      rating: averageRating ? parseFloat(averageRating.toFixed(1)) : null,
      percentage: percentage ? parseFloat(percentage.toFixed(0)) : null,
    };
  };

  useEffect(() => {
    const selfRatingData = calculateRatedPercentage('selfRating');
    const lineManagerRatingData = calculateRatedPercentage('lineManagerRating');
    setStandardsRatings({ selfRatingData, lineManagerRatingData });
  }, [standards, setStandardsRatings]);

  const weightTotal = standards.reduce((sum, standard) => sum + parseFloat(standard.weight || 0), 0);
  const selfRatingData = calculateRatedPercentage('selfRating');
  const lineManagerRatingData = calculateRatedPercentage('lineManagerRating');

  const renderStars = (rating) => {
    if (rating === null) return 'Not Rated';
    return [...Array(5)].map((_, i) => (
      <i key={i} className={`bi ${i < rating ? 'bi-star-fill' : 'bi-star'} text-warning me-1`} />
    ));
  };

  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center mb-3 bg-tertiary p-2 text-light rounded">
        <h4 className="mb-0">Section B: Our Principles & Standards</h4>
        <button type="button" className="btn btn-primary ms-auto" onClick={() => setShowStandardModal(true)}>
          Add New Standard
        </button>
      </div>
      <div className="row g-3">
        <div className="col-lg-4 col-md-6">
          <div className="card h-100">
            <div className="card-body">
              <label htmlFor="standardScore" className="form-label mb-2">Generic Averages (%)</label>
              <input
                type="number"
                className="form-control mb-3"
                id="standardScore"
                value="20"
                readOnly
                disabled
              />
              <label htmlFor="weightTotal" className="form-label mb-2">Weight Total</label>
              <input
                type="number"
                className="form-control"
                id="weightTotal"
                value={weightTotal || 0}
                readOnly
              />
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title">Self Rating</h5>
              <div className="d-flex align-items-center mb-2">
                <div className="me-3">{renderStars(selfRatingData.rating)}</div>
                <span>{selfRatingData.rating !== null ? `${selfRatingData.rating}/5` : 'Not Rated'}</span>
</div>
<div className="progress" style={{ height: '20px' }}>
    <div
        className="progress-bar"
        role="progressbar"
        style={{ width: selfRatingData.percentage ? `${selfRatingData.percentage}%` : '0%' }}
        aria-valuenow={selfRatingData.percentage || 0}
        aria-valuemin="0"
        aria-valuemax="100"
    >
        {selfRatingData.percentage !== null ? `${selfRatingData.percentage}%` : 'Not Rated'}
    </div>
</div>
</div>
</div>
</div>
<div className="col-lg-4 col-md-6">
    <div className="card h-100">
        <div className="card-body">
            <h5 className="card-title">Line Manager Rating</h5>
            <div className="d-flex align-items-center mb-2">
                <div className="me-3">{renderStars(lineManagerRatingData.rating)}</div>
                <span>{lineManagerRatingData.rating !== null ? `${lineManagerRatingData.rating}/5` : 'Not Rated'}</span>
            </div>
            <div className="progress" style={{ height: '20px' }}>
                <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: lineManagerRatingData.percentage ? `${lineManagerRatingData.percentage}%` : '0%' }}
                    aria-valuenow={lineManagerRatingData.percentage || 0}
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                    {lineManagerRatingData.percentage !== null ? `${lineManagerRatingData.percentage}%` : 'Not Rated'}
                </div>
            </div>
        </div>
    </div>
</div>
</div>
<div className="mt-4">
    <table className="table table-bordereds">
        <thead className="bg-tertiary text-light rounded">
        <tr>
            <th>Generic</th>
            <th>Guidelines</th>
            <th>Weight</th>
            <th>Self Rating</th>
            <th>Line Manager Rating</th>
            <th>Action</th>
        </tr>
        </thead>
        <tbody>
        {standards.length === 0 ? (
            <tr>
                <td colSpan="6" className="text-center">No Standards added</td>
            </tr>
        ) : (
            standards.map((standard, index) => (
                <tr key={`standard-${index}`}>
                    <td>
                    <textarea
                        className="form-control"
                        value={standard.generic}
                        readOnly
                        disabled
                        rows={4}
                    />
                    </td>
                    <td>
                    <textarea
                        className="form-control"
                        value={standard.guidelines}
                        readOnly
                        disabled
                        rows={4}
                    />
                    </td>
                    <td>
                        <input
                            type="number"
                            className="form-control"
                            value={standard.weight}
                            readOnly
                            disabled
                        />
                    </td>
                    <td>
                        <select
                            disabled={isSelfAssessment}
                            className="form-select"
                            value={standard.selfRating || ''}
                            onChange={(e) => handleRatingChange(index, 'selfRating', e.target.value)}
                        >
                            <option value="">Select Rating</option>
                            {ratingOptions.map((val) => (
                                <option key={val} value={val}>{val}</option>
                            ))}
                        </select>
                    </td>
                    <td>
                        <select
                            className="form-select"
                            value={standard.lineManagerRating || ''}
                            onChange={(e) => handleRatingChange(index, 'lineManagerRating', e.target.value)}
                            disabled={isSelfAssessment}
                        >
                            <option value="">Select Rating</option>
                            {ratingOptions.map((val) => (
                                <option key={val} value={val}>{val}</option>
                            ))}
                        </select>
                    </td>
                    <td>
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleEditStandard(index)}
                        >
                            Edit
                        </button>
                    </td>
                </tr>
            ))
        )}
        </tbody>
    </table>
</div>
<div className={`modal fade ${showStandardModal ? 'show d-block' : ''}`} style={{ backgroundColor: showStandardModal ? 'rgba(0,0,0,0.5)' : 'transparent' }}>
    <div className="modal-dialog">
        <div className="modal-content">
            <div className="modal-header">
                <h5 className="modal-title">{isEditingStandard ? 'Edit Standard' : 'Add New Standard'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowStandardModal(false)} />
            </div>
            <div className="modal-body">
                <div className="mb-3">
                    <label htmlFor="standardGeneric" className="form-label">Generic</label>
                    <textarea
                        disabled={isSelfAssessment}
                        className="form-control"
                        id="standardGeneric"
                        value={newStandard.generic}
                        onChange={(e) => setNewStandard({ ...newStandard, generic: e.target.value })}
                        rows={4}
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="standardGuidelines" className="form-label">Guidelines</label>
                    <textarea
                        disabled={isSelfAssessment}
                        className="form-control"
                        id="standardGuidelines"
                        value={newStandard.guidelines}
                        onChange={(e) => setNewStandard({ ...newStandard, guidelines: e.target.value })}
                        rows={4}
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="standardWeight" className="form-label">Weight</label>
                    <input
                        type="number"
                        className="form-control"
                        id="standardWeight"
                        value={newStandard.weight}
                        onChange={(e) => setNewStandard({ ...newStandard, weight: e.target.value })}
                        min="0"
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="standardSelfRating" className="form-label">Self Rating (Optional)</label>
                    <select
                        className="form-select"
                        value={newStandard.selfRating || ''}
                        onChange={(e) => setNewStandard({ ...newStandard, selfRating: e.target.value })}
                    >
                        <option value="">Select Rating</option>
                        {ratingOptions.map((val) => (
                            <option key={val} value={val}>{val}</option>
                        ))}
                    </select>
                </div>
                <div className="mb-3">
                    <label htmlFor="standardLineManagerRating" className="form-label">Line Manager Rating (Optional)</label>
                    <select
                        className="form-select"
                        value={newStandard.lineManagerRating || ''}
                        onChange={(e) => setNewStandard({ ...newStandard, lineManagerRating: e.target.value })}
                        disabled={isSelfAssessment}
                    >
                        <option value="">Select Rating</option>
                        {ratingOptions.map((val) => (
                            <option key={val} value={val}>{val}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="modal-footer">
                <div className="d-flex w-100">
                    <button
                        type="button"
                        className="btn btn-danger btn-sm me-auto"
                        onClick={handleDeleteStandard}
                        style={{ visibility: isEditingStandard ? 'visible' : 'hidden' }}
                    >
                        Delete
                    </button>
                    <div>
                        <button type="button" className="btn btn-secondary me-2" onClick={() => setShowStandardModal(false)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={isEditingStandard ? handleSaveEditStandard : handleAddStandard}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</div>
);
}

export default PrinciplesAndStandards;